import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadialBarChart, RadialBar
} from 'recharts';
import { FiSend, FiCheckCircle, FiXCircle, FiUsers, FiDatabase, FiActivity, FiTrendingUp, FiArrowUpRight } from 'react-icons/fi';

/* ── Palette ── */
const C = {
  sky: '#7ea52e', skyLight: '#eef3e6', skyMid: '#a4c44e',
  purple: '#1a1a18', purpleLight: '#f0f0ed',
  green: '#7ea52e', greenLight: '#eef3e6',
  orange: '#9ec25a', orangeLight: '#e5edda',
  red: '#1a1a18', redLight: '#f0f0ed',
  slate: '#c9c9c3', cyan: '#b7d083', pink: '#5c7a1f',
};

const TYPE_COLORS = {
  whatsapp: '#7ea52e', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
  whatsapp_document: '#455a19', sms: '#9ec25a', email: '#1a1a18',
  rcs: '#b7d083', newsletter: '#5c7a1f', push_notification: '#7e7e71',
  voice_call: '#abab9f', status_messaging: '#161614', custom: '#c9c9c3'
};
const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
  rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
  voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
};

/* ── Glassmorphism Tooltip ── */
const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.15)', borderRadius: 14,
      padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(126,165,46,0.06)',
    }}>
      <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif', letterSpacing: 0.3 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill, display: 'inline-block' }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Custom Pie Center Label ── */
const PieCenter = ({ cx, cy, label, value }) => (
  <g>
    <text x={cx} y={cy - 8} textAnchor="middle" style={{ fontSize: 22, fontWeight: 800, fill: '#0f172a', fontFamily: 'Poppins, sans-serif' }}>{value}</text>
    <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 11, fontWeight: 600, fill: '#94a3b8', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</text>
  </g>
);

/* ── Custom Legend ── */
const CustomLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 8 }}>
    {payload?.map((entry, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: entry.color, display: 'inline-block' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{entry.value}</span>
      </div>
    ))}
  </div>
);

/* ── Custom Pie Legend ── */
const PieLegend = ({ data }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' }}>
    {data.map((d, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: 'var(--bg-primary)', transition: 'all 0.15s' }}>
        <span style={{ width: 12, height: 12, borderRadius: 4, background: d.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#334155' }}>{d.name}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{d.value?.toLocaleString()}</span>
      </div>
    ))}
  </div>
);

/* ── SVG Gradient Definitions ── */
const ChartGradients = () => (
  <defs>
    <linearGradient id="adminGradSent" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.4} />
      <stop offset="100%" stopColor="#7ea52e" stopOpacity={0.02} />
    </linearGradient>
    <linearGradient id="adminGradFailed" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.25} />
      <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
    </linearGradient>
    <linearGradient id="adminBarGreen" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#9ec25a" />
      <stop offset="100%" stopColor="#7ea52e" />
    </linearGradient>
    <linearGradient id="adminBarRed" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#333330" />
      <stop offset="100%" stopColor="#1a1a18" />
    </linearGradient>
    <linearGradient id="adminBarSky" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#a4c44e" />
      <stop offset="100%" stopColor="#7ea52e" />
    </linearGradient>
    <filter id="barShadow" x="-5%" y="-5%" width="110%" height="120%">
      <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#7ea52e" floodOpacity="0.15" />
    </filter>
  </defs>
);

/* ── Heatmap ── */
const DAYS_MAP = { 1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat' };
const HOUR_SLOTS = [6, 8, 10, 12, 14, 16, 18, 20, 22];
const HOUR_LABELS = { 6: '6 AM', 8: '8 AM', 10: '10 AM', 12: '12 PM', 14: '2 PM', 16: '4 PM', 18: '6 PM', 20: '8 PM', 22: '10 PM' };
const DAYS_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildHeatmap(hourlyStats) {
  const grid = {};
  DAYS_ORDER.forEach(d => { grid[d] = {}; HOUR_SLOTS.forEach(h => { grid[d][h] = 0; }); });
  (hourlyStats || []).forEach(({ _id, count }) => {
    const dayName = DAYS_MAP[_id.day];
    const hour = _id.hour;
    const slot = HOUR_SLOTS.reduce((prev, curr) => Math.abs(curr - hour) < Math.abs(prev - hour) ? curr : prev);
    if (dayName && grid[dayName]) grid[dayName][slot] += count;
  });
  const maxVal = Math.max(1, ...Object.values(grid).flatMap(d => Object.values(d)));
  return { grid, maxVal };
}

function heatColor(pct) {
  if (pct <= 0) return 'rgba(241,245,249,0.7)';
  if (pct < 25) return 'rgba(126,165,46,0.12)';
  if (pct < 50) return 'rgba(126,165,46,0.28)';
  if (pct < 75) return 'rgba(126,165,46,0.5)';
  return 'rgba(126,165,46,0.75)';
}

/* ── Section Header ── */
const SectionHeader = ({ title, subtitle, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    {icon && <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #eef3e6, #f0f0ed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sky, fontSize: 15 }}>{icon}</div>}
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, fontWeight: 500 }}>{subtitle}</p>}
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!data) return null;

  const { stats = {}, dailyStats = [], recentAudit = [], channelBreakdown = [], topCampaigns = [], hourlyStats = [] } = data;

  /* Trend */
  const days = [...new Set((dailyStats || []).map(d => d._id.date))].sort();
  let trendData = days.map(d => ({
    date: new Date(d).toLocaleDateString('en', { weekday: 'short', day: 'numeric' }),
    Sent: dailyStats.find(s => s._id.date === d && s._id.status === 'sent')?.count || 0,
    Failed: dailyStats.find(s => s._id.date === d && s._id.status === 'failed')?.count || 0
  }));
  if (trendData.length === 1) {
    trendData = [{ date: '', Sent: 0, Failed: 0 }, trendData[0], { date: ' ', Sent: 0, Failed: 0 }];
  } else if (trendData.length === 2) {
    trendData = [{ date: '', Sent: 0, Failed: 0 }, ...trendData];
  }

  /* Channel Pie */
  const channelData = (channelBreakdown || []).map(ch => ({
    name: TYPE_LABELS[ch._id] || ch._id,
    value: (ch.totalSent || 0) + (ch.totalFailed || 0),
    color: TYPE_COLORS[ch._id] || C.slate
  })).filter(c => c.value > 0);
  if (channelData.length === 0) channelData.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const channelTotal = channelData.reduce((a, c) => a + c.value, 0);

  /* Funnel */
  const totalSent = stats.totalSent || 0;
  const totalFailed = stats.totalFailed || 0;
  const funnelData = [
    { stage: 'Total Processed', value: totalSent + totalFailed, fill: 'url(#adminBarSky)' },
    { stage: 'Delivered', value: totalSent, fill: 'url(#adminBarGreen)' },
    { stage: 'Failed', value: totalFailed, fill: 'url(#adminBarRed)' },
    { stage: 'Today Sent', value: stats.todaySent || 0, fill: C.purple },
  ].filter(d => d.value > 0);

  /* Top Campaigns */
  const comparisonData = (topCampaigns || []).map(c => ({
    name: (c.name || '').substring(0, 14),
    Sent: c.totalSent || 0,
    Failed: c.totalFailed || 0,
    Assigned: c.totalAssigned || 0,
  }));

  /* Heatmap */
  const { grid: heatmapGrid, maxVal } = buildHeatmap(hourlyStats);

  const totalMessages = totalSent + totalFailed;
  const successRate = totalMessages > 0 ? ((totalSent / totalMessages) * 100).toFixed(1) : '0';

  /* Delivery gauge */
  const gaugeData = [{ name: 'Delivery', value: parseFloat(successRate), fill: parseFloat(successRate) >= 90 ? C.green : parseFloat(successRate) >= 70 ? C.orange : C.red }];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Overview</h1>
          <p className="page-subtitle">Campaign performance at a glance</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ padding: '8px 18px', borderRadius: 12, background: 'linear-gradient(135deg, #eef3e6, #e5edda)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiArrowUpRight size={14} color={C.green} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#455a19' }}>{successRate}% Delivery</span>
          </div>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="stats-grid">
        {[
          { icon: <FiSend />, value: stats.totalCampaigns || 0, label: 'Total Broadcasts', cls: 'purple', accent: '#1a1a18' },
          { icon: <FiActivity />, value: stats.activeCampaigns || 0, label: 'Active Broadcasts', cls: 'cyan', accent: '#9ec25a' },
          { icon: <FiCheckCircle />, value: (stats.totalSent || 0).toLocaleString(), label: 'Messages Delivered', cls: 'green', accent: '#7ea52e' },
          { icon: <FiXCircle />, value: (stats.totalFailed || 0).toLocaleString(), label: 'Messages Failed', cls: 'red', accent: '#1a1a18' },
          { icon: <FiUsers />, value: stats.totalUsers || 0, label: 'Team Members', cls: 'amber', accent: '#5c7a1f' },
          { icon: <FiDatabase />, value: (stats.totalMasterData || 0).toLocaleString(), label: 'Data Records', cls: 'pink', accent: '#455a19' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${s.accent}` }}>
            <div className={`stat-card-icon ${s.cls}`}>{s.icon}</div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Row 1: Performance Trend + Channel Donut ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="chart-container">
          <SectionHeader title="Performance Trend" subtitle="7-day delivery & failure overview" icon={<FiTrendingUp />} />
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="admAreaSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.45} />
                    <stop offset="50%" stopColor="#9ec25a" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#b7d083" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="admAreaFailed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.35} />
                    <stop offset="50%" stopColor="#333330" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Legend content={<CustomLegend />} />
                <Area type="natural" dataKey="Sent" stackId="adm" stroke="#7ea52e" fill="url(#admAreaSent)" strokeWidth={3}
                  dot={{ r: 5, fill: '#fff', stroke: '#7ea52e', strokeWidth: 2.5 }}
                  activeDot={{ r: 8, fill: '#7ea52e', stroke: '#fff', strokeWidth: 3 }}
                  animationDuration={1200} animationEasing="ease-in-out" />
                <Area type="natural" dataKey="Failed" stackId="adm" stroke="#1a1a18" fill="url(#admAreaFailed)" strokeWidth={2.5}
                  dot={{ r: 4, fill: '#fff', stroke: '#1a1a18', strokeWidth: 2 }}
                  activeDot={{ r: 7, fill: '#1a1a18', stroke: '#fff', strokeWidth: 3 }}
                  animationDuration={1500} animationEasing="ease-in-out" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📈</div><h3>No trend data</h3><p>Send messages to see trends</p></div>
          )}
        </div>

        <div className="chart-container">
          <SectionHeader title="Channel Distribution" subtitle="Messages by channel type" />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={channelData} cx="50%" cy="50%" innerRadius={58} outerRadius={88} paddingAngle={3} dataKey="value"
                  stroke="none" cornerRadius={4}>
                  {channelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<GlassTooltip />} />
                {channelTotal > 0 && <PieCenter cx="50%" cy="50%" label="Messages" value={channelTotal.toLocaleString()} />}
              </PieChart>
            </ResponsiveContainer>
            <PieLegend data={channelData} />
          </div>
        </div>
      </div>

      {/* ── Row 2: Funnel + Top Campaigns ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20, marginBottom: 20 }}>
        <div className="chart-container">
          <SectionHeader title="Message Funnel" subtitle="Processing pipeline overview" />
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={funnelData} layout="vertical" barSize={28} margin={{ left: 10 }}>
                <defs>
                  <filter id="admFunnelShadow" x="-6%" y="-6%" width="112%" height="120%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#7ea52e" floodOpacity="0.12" />
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="stage" tick={{ fill: '#334155', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip content={<GlassTooltip />} cursor={{ fill: 'rgba(126,165,46,0.04)' }} />
                <Bar dataKey="value" radius={[0, 12, 12, 0]} name="Count" animationDuration={1200} filter="url(#admFunnelShadow)">
                  {funnelData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No funnel data</h3></div>
          )}
        </div>

        <div className="chart-container">
          <SectionHeader title="Top Campaigns" subtitle="Best performing broadcasts" />
          {comparisonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={comparisonData} barSize={36} barCategoryGap="20%" margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="admCmpG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#b7d083" /><stop offset="100%" stopColor="#7ea52e" />
                  </linearGradient>
                  <linearGradient id="admCmpR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#444440" /><stop offset="100%" stopColor="#1a1a18" />
                  </linearGradient>
                  <linearGradient id="admCmpS" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d4e6a8" /><stop offset="100%" stopColor="#9ec25a" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 8" stroke="#e9ecef" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} cursor={{ fill: 'rgba(126,165,46,0.04)', radius: 4 }} />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="Sent" stackId="admCmp" fill="url(#admCmpG)" animationDuration={1000} animationEasing="ease-out" />
                <Bar dataKey="Failed" stackId="admCmp" fill="url(#admCmpR)" animationDuration={1200} animationEasing="ease-out" />
                <Bar dataKey="Assigned" stackId="admCmp" fill="url(#admCmpS)" radius={[6, 6, 0, 0]} animationDuration={1400} animationEasing="ease-out" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No campaign data</h3></div>
          )}
        </div>
      </div>

      {/* ── Row 3: Heatmap + Delivery Gauge + Activity ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.6fr 1fr', gap: 20 }}>
        {/* Heatmap */}
        <div className="chart-container">
          <SectionHeader title="Engagement Heatmap" subtitle="Live delivery times — last 7 days" />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 4 }}>
              <thead>
                <tr>
                  <th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textAlign: 'left', padding: '6px 8px' }}></th>
                  {HOUR_SLOTS.map(h => <th key={h} style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textAlign: 'center', padding: '6px 2px' }}>{HOUR_LABELS[h]}</th>)}
                </tr>
              </thead>
              <tbody>
                {DAYS_ORDER.map(day => (
                  <tr key={day}>
                    <td style={{ fontSize: 11, color: '#334155', fontWeight: 700, padding: '6px 8px' }}>{day}</td>
                    {HOUR_SLOTS.map(h => {
                      const val = heatmapGrid[day]?.[h] || 0;
                      const pct = maxVal > 0 ? Math.round((val / maxVal) * 100) : 0;
                      return (
                        <td key={h} title={`${day} ${HOUR_LABELS[h]}: ${val} msgs`}
                          style={{
                            background: heatColor(pct),
                            borderRadius: 6, textAlign: 'center', padding: '10px 4px',
                            fontSize: 10, fontWeight: 700,
                            color: pct > 50 ? '#455a19' : pct > 0 ? '#6b8f27' : '#c9c9c3',
                            minWidth: 38, cursor: 'default',
                            transition: 'all 0.2s',
                          }}>
                          {val || '·'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Heatmap legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Less</span>
            {[0, 25, 50, 75, 100].map(p => (
              <span key={p} style={{ width: 14, height: 14, borderRadius: 3, background: heatColor(p), border: '1px solid rgba(0,0,0,0.04)' }} />
            ))}
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>More</span>
          </div>
        </div>

        {/* Delivery Gauge */}
        <div className="chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <SectionHeader title="Delivery Rate" />
          <ResponsiveContainer width="100%" height={180}>
            <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={gaugeData} barSize={14}>
              <RadialBar background={{ fill: '#f4f4f3' }} dataKey="value" cornerRadius={10} animationDuration={1500} />
              <Tooltip content={<GlassTooltip />} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: -30 }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: gaugeData[0].fill, fontFamily: 'Poppins' }}>{successRate}%</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>SUCCESS RATE</div>
          </div>
        </div>

        {/* Activity */}
        <div className="card">
          <SectionHeader title="Recent Activity" subtitle="Latest operations" />
          {recentAudit?.length > 0 ? (
            <div className="timeline">
              {recentAudit.slice(0, 6).map((log, i) => (
                <div key={i} className="timeline-item">
                  <div className="timeline-dot" style={{ background: i === 0 ? C.sky : undefined }} />
                  <div className="timeline-content">
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{log.action}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{log.username} — {log.details}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(log.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No activity yet</h3></div>
          )}
        </div>
      </div>
    </div>
  );
}
